/**
 * Walker's Algorithm (1989) for tree layout positioning.
 * Adapted from acc-wc-view-los-graph/walkerLayout.js for use with TreeView.
 *
 * This algorithm produces aesthetically pleasing tree layouts by:
 * 1. Centering parents over their children
 * 2. Maintaining consistent spacing between siblings and subtrees
 * 3. Avoiding node overlaps through the "apportion" step
 */

import type { TreeNodeData } from "../TreeViewDataLayer";

/**
 * Internal node representation for Walker's algorithm.
 * Uses Knuth's threaded tree structure for efficient traversal.
 */
export interface WalkerNode {
  id: number;
  parent: WalkerNode | null;
  leftSibling: WalkerNode | null;
  rightSibling: WalkerNode | null;
  leftNeighbor: WalkerNode | null;
  children: WalkerNode[];
  x: number;
  y: number;
  preliminary: number;
  modifier: number;
  level: number;
  originalData: TreeNodeData;
}

/**
 * Configuration for the Walker algorithm
 */
export interface WalkerLayoutConfig {
  nodeWidth: number;
  nodeHeight: number;
  siblingSeparation: number;
  subtreeSeparation: number;
  levelSeparation: number;
}

/**
 * Result of the layout calculation
 */
export interface NodePosition {
  abo: number;
  x: number;
  y: number;
  originalData: TreeNodeData;
}

/**
 * Creates a new WalkerNode with default values
 */
const createWalkerNode = (id: number, originalData: TreeNodeData): WalkerNode => ({
  id,
  parent: null,
  leftSibling: null,
  rightSibling: null,
  leftNeighbor: null,
  children: [],
  x: 0,
  y: 0,
  preliminary: 0,
  modifier: 0,
  level: 0,
  originalData,
});

/**
 * Returns the next node in preorder traversal
 */
const getNextInPreorder = (node: WalkerNode): WalkerNode | null => {
  if (node.children.length > 0) {
    return node.children[0];
  }

  let current: WalkerNode | null = node;
  while (current?.parent && !current.rightSibling) {
    current = current.parent;
  }
  return current?.rightSibling ?? null;
};

/**
 * Returns the next node in postorder traversal
 */
const getNextInPostorder = (node: WalkerNode): WalkerNode | null => {
  if (node.rightSibling) {
    let current: WalkerNode = node.rightSibling;
    while (current.children.length > 0) {
      current = current.children[0];
    }
    return current;
  }
  return node.parent;
};

/**
 * Gets the leftmost descendant at a given depth
 */
const getLeftMost = (node: WalkerNode, depth: number): WalkerNode | null => {
  if (depth <= 0) {
    return node;
  }
  if (node.children.length === 0) {
    return null;
  }

  let ancestor: WalkerNode | null = node.children[0];
  let leftMost = getLeftMost(ancestor, depth - 1);

  while (!leftMost && ancestor?.rightSibling) {
    ancestor = ancestor.rightSibling;
    leftMost = getLeftMost(ancestor, depth - 1);
  }

  return leftMost;
};

/**
 * Calculates and returns the level (depth) of a node
 */
const calculateLevel = (node: WalkerNode): number => {
  if (node.level > 0) {
    return node.level;
  }

  let level = 0;
  let current: WalkerNode | null = node;
  while (current?.parent) {
    level += 1;
    current = current.parent;
  }

  node.level = level;
  return level;
};

/**
 * Applies callback to each node in preorder
 */
const eachInPreorder = (root: WalkerNode, callback: (node: WalkerNode) => void): void => {
  let node: WalkerNode | null = root;
  while (node) {
    callback(node);
    node = getNextInPreorder(node);
  }
};

/**
 * Applies callback to each node in postorder
 */
const eachInPostorder = (root: WalkerNode, callback: (node: WalkerNode) => void): void => {
  // Find the leftmost leaf
  let node: WalkerNode | null = root;
  while (node && node.children.length > 0) {
    node = node.children[0];
  }

  while (node) {
    callback(node);
    node = getNextInPostorder(node);
  }
};

/**
 * First walk: bottom-up pass to calculate preliminary positions
 */
const firstWalk = (node: WalkerNode, config: WalkerLayoutConfig): void => {
  if (node.children.length === 0) {
    // Leaf node
    if (node.leftSibling) {
      node.preliminary = node.leftSibling.preliminary + config.siblingSeparation + config.nodeWidth;
    } else {
      node.preliminary = 0;
    }
  } else {
    // Internal node
    const leftMost = node.children[0];
    const rightMost = node.children[node.children.length - 1];
    const middle = (leftMost.preliminary + rightMost.preliminary) / 2;

    if (node.leftSibling) {
      node.preliminary = node.leftSibling.preliminary + config.siblingSeparation + config.nodeWidth;
      node.modifier = node.preliminary - middle;
      apportion(node, config);
    } else {
      node.preliminary = middle;
    }
  }
};

/**
 * Apportion: resolve subtree conflicts
 */
const apportion = (node: WalkerNode, config: WalkerLayoutConfig): void => {
  let leftMost: WalkerNode | null = node;
  let neighbor: WalkerNode | null = node.leftNeighbor;
  let depth = 0;

  while (leftMost && neighbor) {
    let leftModifier = 0;
    let rightModifier = 0;
    let ancestorLeftMost: WalkerNode | null = leftMost;
    let ancestorNeighbor: WalkerNode | null = neighbor;

    for (let i = 0; i < depth; i++) {
      ancestorLeftMost = ancestorLeftMost?.parent ?? null;
      ancestorNeighbor = ancestorNeighbor?.parent ?? null;
      if (ancestorLeftMost) rightModifier += ancestorLeftMost.modifier;
      if (ancestorNeighbor) leftModifier += ancestorNeighbor.modifier;
    }

    let moveDistance =
      neighbor.preliminary +
      leftModifier +
      config.nodeWidth +
      config.subtreeSeparation -
      leftMost.preliminary -
      rightModifier;

    if (moveDistance > 0) {
      let tmp: WalkerNode | null = node;
      let leftSiblings = 0;

      while (tmp && tmp !== ancestorNeighbor) {
        leftSiblings += 1;
        tmp = tmp.leftSibling;
      }

      if (tmp) {
        const portion = moveDistance / leftSiblings;
        tmp = node;

        while (tmp && tmp !== ancestorNeighbor) {
          tmp.preliminary += moveDistance;
          tmp.modifier += moveDistance;
          moveDistance -= portion;
          tmp = tmp.leftSibling;
        }
      } else {
        return;
      }
    }

    depth += 1;
    leftMost = getLeftMost(node, depth);
    if (leftMost) {
      neighbor = leftMost.leftNeighbor;
    }
  }
};

/**
 * Second walk: top-down pass to finalize positions
 */
const secondWalk = (
  node: WalkerNode,
  modifier: number,
  xAdjustment: number,
  yAdjustment: number,
  levelSeparation: number,
): void => {
  node.x = xAdjustment + node.preliminary + modifier;
  node.y = yAdjustment + calculateLevel(node) * levelSeparation;

  if (node.children.length > 0) {
    secondWalk(node.children[0], modifier + node.modifier, xAdjustment, yAdjustment, levelSeparation);
  }

  if (node.rightSibling) {
    secondWalk(node.rightSibling, modifier, xAdjustment, yAdjustment, levelSeparation);
  }
};

/**
 * Main positioning function - runs Walker's algorithm on the tree
 */
const positionTree = (root: WalkerNode, config: WalkerLayoutConfig): void => {
  // First walk: bottom-up pass
  eachInPostorder(root, (node) => firstWalk(node, config));

  // Calculate adjustments
  const xAdjustment = root.x - root.preliminary;
  const yAdjustment = root.y;

  // Second walk: top-down pass
  secondWalk(root, 0, xAdjustment, yAdjustment, config.levelSeparation);
};

/**
 * Parse TreeNodeData into Walker's internal tree structure.
 * Only includes expanded nodes and their visible children.
 */
const parseTreeData = (rootData: TreeNodeData): WalkerNode => {
  // Build nodes recursively, only including expanded branches
  const buildNode = (data: TreeNodeData): WalkerNode => {
    const node = createWalkerNode(data.abo, data);

    if (data.expanded && data.downlines?.length) {
      data.downlines.forEach((childData) => {
        const childNode = buildNode(childData);
        childNode.parent = node;
        node.children.push(childNode);
      });
    }

    return node;
  };

  const root = createWalkerNode(rootData.abo, rootData);
  if (rootData.expanded && rootData.downlines?.length) {
    rootData.downlines.forEach((childData) => {
      const childNode = buildNode(childData);
      childNode.parent = root;
      root.children.push(childNode);
    });
  }

  // Set up sibling and neighbor relationships using BFS
  const queue: WalkerNode[] = [];
  root.children.forEach((child) => queue.push(child));

  let leftSibling: WalkerNode | null = null;
  let leftNeighbor: WalkerNode | null = null;

  while (queue.length > 0) {
    const node = queue.shift();
    if (!node) break;

    node.children.forEach((child) => queue.push(child));

    const nextNode = queue[0] ?? null;
    const rightSibling = nextNode && node.parent === nextNode.parent ? nextNode : null;

    node.leftSibling = leftSibling;
    node.rightSibling = rightSibling;
    node.leftNeighbor = leftNeighbor;

    // Update for next iteration
    leftSibling = nextNode && node.parent === nextNode.parent ? node : null;
    if (leftSibling !== null) {
      leftNeighbor = leftSibling;
    } else {
      leftNeighbor = nextNode && calculateLevel(node) === calculateLevel(nextNode) ? node : null;
    }
  }

  return root;
};

/**
 * Calculate positions for all nodes using Walker's algorithm.
 *
 * @param rootData - The root TreeNodeData
 * @param config - Layout configuration
 * @param rootX - X position for the root node (tree will be centered here)
 * @param rootY - Y position for the root node
 * @returns Array of node positions
 */
export const calculateWalkerLayout = (
  rootData: TreeNodeData,
  config: WalkerLayoutConfig,
  rootX: number,
  rootY: number,
): NodePosition[] => {
  const root = parseTreeData(rootData);

  // Set initial root position
  root.x = rootX;
  root.y = rootY;

  // Run Walker's algorithm
  positionTree(root, config);

  // Collect results
  const positions: NodePosition[] = [];

  eachInPreorder(root, (node) => {
    positions.push({
      abo: node.id,
      x: node.x,
      y: node.y,
      originalData: node.originalData,
    });
  });

  return positions;
};

/**
 * Default configuration matching the existing TreeView layout
 */
export const DEFAULT_WALKER_CONFIG: WalkerLayoutConfig = {
  nodeWidth: 260,
  nodeHeight: 172,
  siblingSeparation: 44,
  subtreeSeparation: 88,
  levelSeparation: 232,
};
